import { AxiosRequestConfig } from "axios";
import {
  EntityPortalService,
  PortalServiceEnum,
} from "@/core/services/api/interfaces/dict/PortalService";
import { CurrencyUnit } from "@/core/services/api/interfaces/manager/CurrencyUnit";
import { TransmissionProtocol } from "@/core/services/api/interfaces/manager/TransmissionProtocol";
import { FlexpspAccountPaymentType } from "@/interfaces/manager/PSPEntities";
import { TransferChannel } from "@/core/services/api/interfaces/dict/TransferChannel";
import { ExchangeService } from "@/core/services/api/interfaces/dict/ExchangeService";
import { FlexpspPaymentSystem } from "@/interfaces/manager/PSPEntities";

export interface RequisitesItem {
  accountId?: string | null;
  accountHolderName?: string | null;
  accountNumber?: string | null;
  accountNumberType?: string | null;
  bankAddress?: string[] | string | null;
  bankCountry?: string | null;
  bankName?: string | null;
  beneficiaryAddress?: string | null;
  beneficiaryCountry?: string | null;
  currency?: string | null;
  routingCode?: string | null;
  routingCodeType?: string | null;
  localBankName?: string | null;
  localPayeeName?: string | null;
  localBankIdentifier?: string | null;
  localBankAddressCity?: string | null;
  localBankAddressAddressLine1?: string | null;
  localBankAddressAddressLine2?: string | null;
  localBankAddressAddressLine3?: string | null;
  localAccountIdentifier?: string | null;
  localBankAddressCountry?: string | null;
  localBankAddressPostcode?: string | null;
  internationalBankName?: string | null;
  internationalPayeeName?: string | null;
  internationalBankIdentifier?: string | null;
  internationalBankAddressCity?: string | null;
  internationalAccountIdentifier?: string | null;
  internationalBankAddressCountry?: string | null;
  internationalBankAddressPostcode?: string | null;
  internationalBankAddressAddressLine1?: string | null;
  internationalBankAddressAddressLine2?: string | null;
  internationalBankAddressAddressLine3?: string | null;
  bankCode?: string | null;
  bankCodeType?: string | null;
  name?: string | null;
  bicSwift?: string | null;
  iban?: string | null;
  beneficiaryName?: string | null;
  sortCode?: string | null;
  sortCode1?: string | null;
  countryCode?: string | null;
  countryCode1?: string | null;
  accountNumber1?: string | null;
  accountHolderName1?: string | null;
  intermediarySortCode?: string | null;
  intermediarySortCode1?: string | null;
  intermediaryCountryCode?: string | null;
  intermediaryCountryCode1?: string | null;
  intermediaryAccountNumber?: string | null;
  intermediaryAccountNumber1?: string | null;
  intermediaryAccountHolderName?: string | null;
  intermediaryAccountHolderName1?: string | null;
  //
  // {
  //   "type": "SortCodeAccountNumber",
  //   "currency": "EUR",
  //   "sortCode": "609242",
  //   "countryCode": "GB",
  //   "accountNumber": "41480141",
  //   "accountHolderName": "FreemarketFX Limited"
  // }
}

export interface TemporaryIban {
  id: number;
  iban: string;
  reference: string | null;
  expires_at: string | null;
}

export interface PspPortalAccount {
  account_type: FlexpspAccountPaymentType;
  account_fields: {
    id: number;
    active: boolean;
  };
}

export interface FlexpspAccountIdentification {
  id: number;
  type: string | null;
  identification: string | null;
  flexpsp_id: string | null;
}

export interface FlexpspAccount {
  id: number;
  bank_account_id: number;
  company_id: number;
  flexpsp_payment_system: FlexpspPaymentSystem | null;
  flexpsp_id: string | null;
  flexpsp_account_payment_type: FlexpspAccountPaymentType | null;
  identifications: FlexpspAccountIdentification[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BankAccount {
  id: number;
  name: string;
  readable_name: string;
  currency: string;
  currency_unit: CurrencyUnit;
  requisites: RequisitesItem[];
  temporary_ibans: TemporaryIban[];
  iban: string;
  available_balance: number;
  total_balance: number;
  pending_balance: number;
  selected: boolean;
  status: number;
  readable_status: string;
  psp_portal_domestic_id: null | string;
  psp_portal_domestic_active: boolean;
  psp_portal_international_id: null | string;
  psp_portal_international_active: boolean;
  psp_portal_accounts: PspPortalAccount[];
  portal_services: { [index: string]: EntityPortalService };
  portal_service_active: string | null;
  portal_service_active_name: string | null;
  transmission_protocols_disabled: TransmissionProtocol[];
  transfer_channel: TransferChannel;
  support_transfers: number[];
  support_exchanges: number[];
  exchange_service: null | ExchangeService;
  actions: {
    is_crypto_buy: boolean;
    is_crypto_sell: boolean;
    is_crypto_deposit: boolean;
    is_fiat_withdraw: boolean;
    has_wire_instructions: boolean;
  };
  approximate_balances: {
    total: {
      currency_unit: CurrencyUnit;
      amount: string;
    };
    crypto: {
      currency_unit: CurrencyUnit;
      amount: string;
    };
    fiat: {
      currency_unit: CurrencyUnit;
      amount: string;
    };
  };
  portal_auto_transfer_enabled: boolean;
  portal_auto_transfer_min_amount: number | null;
  portal_auto_transfer_account_id: string | null;
  portal_auto_transfer_service: PortalServiceEnum | null;
}

export interface RemotePortalBankAccount {
  id: string;
  name: string;
  readable_name: string;
  currency: string;
  currency_unit: CurrencyUnit;
}

export interface CreateBankAccount extends AxiosRequestConfig {
  name: string | null;
  currency: string | null;
}

export interface PortalServiceCompanySignUp
  extends PortalServiceEmployeeSignUp {
  company_name: string;
}

export interface PortalServiceEmployeeSignUp extends AxiosRequestConfig {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  password_confirmation: string;
  address: string;
  city: string;
  country: string;
  phone: string;
  agree: boolean;
  portal_service: string;
}

export enum AutoTransferType {
  ON_EVERY_TRANSACTION = "every_transaction",
  BASED_ON_AMOUNT = "based_on_amount",
}

export interface BankAccountPortalAutoTransfer extends AxiosRequestConfig {
  bank_account_id: number | null;
  portal_auto_transfer_service: PortalServiceEnum | null;
  portal_auto_transfer_account_id: string | null;
  portal_auto_transfer_min_amount: number;
  portal_auto_transfer_enabled: boolean;
  type: AutoTransferType;
}
